/**
 * CRM-MDM-01 — CRM Configuration
 * Settings → Business Setup → CRM Configuration
 *
 * System Administrator-only page for configuring:
 *   1. Urgency Thresholds  (event urgency CRITICAL/HIGH day windows)
 *   2. Co-Pilot Guidance   (per-section / per-page tips in the Co-Pilot drawer)
 *
 * Auth: admin.users capability (TENANT_ADMIN, SETUP_ADMIN).
 * No AI. Deterministic configuration only.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Save, RotateCcw, AlertCircle, CheckCircle2,
  ChevronDown, ChevronUp, Loader2, Settings2, Compass, Info, Clock,
} from "lucide-react";
import { crmMdmApi } from "@/services/api";

// ── Default Co-Pilot tips (mirrors SECTION_GUIDE in CoPilotDrawer.jsx) ────────
const DEFAULT_SECTION_GUIDE = {
  CRM: {
    label: "Sales & CRM",
    tips: [
      "All new leads require owner assignment before qualification can begin",
      "Log every call, meeting and note to maintain a complete audit trail",
      "Qualification requires confirmation of event scope, budget and target date",
    ],
    pages: {
      "Lead Detail": [
        "The Next Best Action panel surfaces recommended actions for this stage",
        "All status transitions are logged and visible in the Activity tab",
      ],
      "Leads": [
        "Filter by status to identify leads needing immediate attention",
        "Use the NBA panel on Lead Detail for workflow-driven recommended actions",
      ],
      "Enquiries": [
        "Web and email form enquiries arrive here for initial triaging",
        "Assign or convert an enquiry to create a qualified lead record",
      ],
      "Communications": [
        "All inbound messages are threaded by lead to maintain context",
        "Mark threads resolved after completing the required follow-up action",
      ],
    },
  },
  OPPORTUNITY: {
    label: "Opportunity Pipeline",
    tips: [
      "Stage progression: Prospecting → Needs Analysis → Proposal → Negotiation → Closed Won",
      "Event design and scope must be confirmed before generating commercial terms",
      "A Booking is created once the opportunity is Closed Won and client accepts terms",
    ],
    pages: {
      "Opportunities": [
        "Filter by stage to prioritise pipeline opportunities requiring action",
        "Stale opportunities (no activity in 14 days) need a follow-up touchpoint",
      ],
      "Opportunity Detail": [
        "Use the Scope tab to attach event requirements to this opportunity",
        "Commercial terms are generated from the accepted scope and service catalog",
        "The Booking tab shows the linked booking once terms are accepted",
      ],
    },
  },
  BOOKING: {
    label: "Bookings",
    tips: [
      "Booking flow: DRAFT → PENDING ACCEPTANCE → CONFIRMED → COMPLETED",
      "Milestones track the payment schedule; each is linked to a planned date",
      "Event planning and operations begin only after booking is CONFIRMED",
    ],
    pages: {
      "Bookings": [
        "Filter by status to see which bookings need submission or confirmation",
        "CONFIRMED bookings can be linked to event planning and operations",
      ],
      "Booking Detail": [
        "Submit the booking from DRAFT to PENDING_ACCEPTANCE for client review",
        "Confirm the booking after client acceptance to unlock event planning",
        "Milestones can only be updated while the booking is CONFIRMED",
      ],
    },
  },
  PLANNING: {
    label: "Event Planning",
    tips: [
      "Event creation requires a confirmed booking from a qualified lead",
      "Cost sheets must be approved by the Event Manager before a proposal is issued",
      "Capture all client requirements before building the cost sheet",
    ],
    pages: {
      "Event Planning": [
        "Use the Requirements panel to capture all client-specified must-haves",
        "Revision history tracks all proposal changes with documented reasons",
      ],
      "Event Design": [
        "The event brief provides the foundation for all scope and commercial decisions",
        "Blueprint approval unlocks the Scope and Requirements tabs for full detail capture",
        "Activate the event once the blueprint is approved and booking is confirmed",
      ],
      "Planning Cases": [
        "Assigned planning cases appear here for the Event Planner role",
        "Link the case to an active booking before starting the cost sheet",
      ],
    },
  },
  EXECUTION: {
    label: "Event Execution",
    tips: [
      "Operations sessions must be opened before checkpoints can be logged",
      "Incidents require severity classification and an assigned owner",
      "Close-out exceptions must be resolved before the fiscal period can be closed",
    ],
    pages: {
      "Active Events": [
        "Events enter ACTIVE state after the planning handoff is confirmed",
        "All operational changes trigger maker-checker approval notifications",
      ],
      "Event Detail": [
        "Review the event status and team assignments before opening an operations session",
        "Scope changes during execution must be logged as formal Change Requests",
        "All stage transitions are audit-logged with actor, timestamp and reason",
      ],
      "Event Operations": [
        "Open an operations session to begin logging operational checkpoints",
        "LIVE status requires at least one open session and all readiness checks complete",
        "Document all incidents immediately — severity escalation follows the SLA matrix",
      ],
      "Event Closeout": [
        "All operational exceptions must be resolved before submitting the closeout report",
        "Post-event financials are locked after closeout submission",
        "Capture testimonial and feedback evidence before marking closeout complete",
      ],
      "Event Financials": [
        "Budget actuals are read-only here — amendments require a formal Change Request",
        "Financial close-out requires Finance Manager approval",
        "All budget variances above threshold require a documented explanation",
      ],
    },
  },
  FINANCE: {
    label: "Payments & Finance",
    tips: [
      "AR: Receipt verification (Tenant Admin) and allocation (Finance Manager) are SoD-separated",
      "AP: GRN delivery confirmation must precede invoice review",
      "GL: Journals must balance (DR = CR) and pass maker-checker approval before posting",
    ],
    pages: {
      "Accounts Receivable": [
        "Invoice flow: DRAFT → ISSUED → PAID. Credit notes reduce the outstanding balance.",
        "Receipts at PENDING_VERIFICATION require Tenant Admin before Finance Manager allocation",
      ],
      "General Ledger": [
        "Journals must balance before submission (total DR = total CR)",
        "POSTED journals can only be corrected by reversal — no direct edits permitted",
      ],
      "Accounts Payable": [
        "GRN submission triggers the invoice review workflow for the Finance Manager",
        "Payment execution requires Finance Manager approval after invoice review",
      ],
    },
  },
  SETTINGS: {
    label: "Business Setup",
    tips: [
      "Legal entity and operating unit setup must be completed before event creation",
      "COA v2 activation requires dual authorization — Business Owner and Finance Manager",
    ],
    pages: {
      "Business Setup": [
        "Complete setup groups in sequence — later steps depend on earlier configuration",
      ],
    },
  },
  ADMINISTRATION: {
    label: "Administration",
    tips: [
      "Role capability changes are governed by the RBAC governance policy",
      "Audit logs are immutable — events cannot be deleted or altered",
    ],
    pages: {
      "Administration": [],
      "Enterprise": [],
    },
  },
};

const SECTION_ORDER = [
  "CRM", "OPPORTUNITY", "BOOKING", "PLANNING",
  "EXECUTION", "FINANCE", "SETTINGS", "ADMINISTRATION",
];

const MAX_TIPS = 10;

// ── Helpers ───────────────────────────────────────────────────────────────────
function tipsToText(tips) {
  return (tips || []).join("\n");
}

function textToTips(text) {
  return text
    .split("\n")
    .map(t => t.trim())
    .filter(Boolean)
    .slice(0, MAX_TIPS);
}

function buildHelpLookup(items) {
  const m = {};
  (items || []).forEach(item => {
    m[item.item_key] = item;
  });
  return m;
}

// ── Toast helper ──────────────────────────────────────────────────────────────
function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return { toast, show };
}

// ── Inline toast ──────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  const isErr = toast.type === "error";
  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border"
      style={{
        background: isErr ? "#FEF2F2" : "#F0FDF4",
        color:      isErr ? "#991B1B" : "#166534",
        borderColor:isErr ? "#FECACA" : "#BBF7D0",
      }}
      data-testid="crm-config-toast"
    >
      {isErr
        ? <AlertCircle size={15} />
        : <CheckCircle2 size={15} />}
      {toast.message}
    </div>
  );
}

// ── Section badge ─────────────────────────────────────────────────────────────
function CustomBadge() {
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
      style={{ background: "#EEF4FF", color: "#0176D3", borderColor: "rgba(1,118,211,0.3)" }}
      data-testid="crm-config-custom-badge"
    >
      Custom
    </span>
  );
}

// ── Tips editor ───────────────────────────────────────────────────────────────
function TipsEditor({ value, onChange, placeholder, testId }) {
  return (
    <textarea
      data-testid={testId}
      className="w-full rounded-lg border text-sm leading-relaxed resize-y min-h-[90px] focus:outline-none focus:ring-2"
      style={{
        borderColor:  "#DDDBDA",
        background:   "#FAFAF9",
        padding:      "10px 12px",
        color:        "#181818",
        focusRingColor: "#0176D3",
        fontFamily:   "inherit",
      }}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || "Enter one tip per line…"}
      rows={4}
      onFocus={e => { e.target.style.borderColor = "#0176D3"; e.target.style.boxShadow = "0 0 0 2px rgba(1,118,211,0.15)"; }}
      onBlur={e  => { e.target.style.borderColor = "#DDDBDA"; e.target.style.boxShadow = "none"; }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Section 1 — Urgency Thresholds
// ══════════════════════════════════════════════════════════════════════════════
function UrgencyThresholdsSection({ toast }) {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [resetting, setResetting] = useState(false);
  const [critical,  setCritical]  = useState("");
  const [high,      setHigh]      = useState("");
  const [error,     setError]     = useState("");

  const load = useCallback(() => {
    setLoading(true);
    crmMdmApi.getUrgencyThresholds()
      .then(r => {
        setData(r.data);
        setCritical(String(r.data.critical_days));
        setHigh(String(r.data.high_days));
      })
      .catch(() => toast.show("Failed to load urgency thresholds", "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  function validate() {
    const c = parseInt(critical, 10);
    const h = parseInt(high, 10);
    if (!Number.isInteger(c) || c <= 0) return "CRITICAL threshold must be a positive integer";
    if (!Number.isInteger(h) || h <= 0) return "HIGH threshold must be a positive integer";
    if (c >= h) return "CRITICAL threshold must be strictly less than HIGH threshold";
    return "";
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setSaving(true);
    try {
      const r = await crmMdmApi.updateUrgencyThresholds(
        parseInt(critical, 10),
        parseInt(high, 10),
      );
      setData(prev => ({ ...prev, ...r.data, is_custom: true }));
      toast.show("Urgency thresholds saved");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save thresholds";
      toast.show(msg, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      const r = await crmMdmApi.resetUrgencyThresholds();
      setData(r.data);
      setCritical(String(r.data.critical_days));
      setHigh(String(r.data.high_days));
      setError("");
      toast.show("Urgency thresholds reset to system defaults");
    } catch {
      toast.show("Failed to reset thresholds", "error");
    } finally {
      setResetting(false);
    }
  }

  const dirty = data &&
    (parseInt(critical, 10) !== data.critical_days ||
     parseInt(high, 10) !== data.high_days);

  return (
    <div
      className="bg-white border border-[#DDDBDA] rounded-xl overflow-hidden"
      data-testid="urgency-thresholds-section"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#F3F2F2] flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#EEF4FF] flex items-center justify-center shrink-0">
          <Clock size={16} className="text-[#0176D3]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-[#181818]">Urgency Thresholds</h2>
            {data?.is_custom && <CustomBadge />}
          </div>
          <p className="text-xs text-[#706E6B] mt-0.5">
            Configure the day-windows that trigger CRITICAL and HIGH urgency alerts on event date recommendations.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="p-6 flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-[#0176D3]" />
        </div>
      ) : (
        <div className="p-5 space-y-5">
          {/* Info strip */}
          <div
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border text-xs"
            style={{ background: "#FFFDF8", borderColor: "#F3E8C0", color: "#7A5C00" }}
          >
            <Info size={13} className="shrink-0 mt-0.5" />
            <span>
              These thresholds apply to all event date urgency recommendations for your tenant.
              System defaults are <strong>CRITICAL = 45 days</strong>, <strong>HIGH = 90 days</strong>.
            </span>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CRITICAL */}
            <div>
              <label
                htmlFor="crm-critical-days"
                className="block text-xs font-semibold text-[#181818] mb-1.5"
              >
                CRITICAL threshold
                <span className="ml-1 font-normal text-[#706E6B]">(days before event)</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="crm-critical-days"
                  data-testid="input-critical-days"
                  type="number"
                  min={1}
                  max={365}
                  value={critical}
                  onChange={e => { setCritical(e.target.value); setError(""); }}
                  className="w-full rounded-lg border text-sm px-3 py-2 focus:outline-none"
                  style={{ borderColor: "#DDDBDA", color: "#181818" }}
                  onFocus={e => { e.target.style.borderColor = "#0176D3"; e.target.style.boxShadow = "0 0 0 2px rgba(1,118,211,0.15)"; }}
                  onBlur={e  => { e.target.style.borderColor = "#DDDBDA"; e.target.style.boxShadow = "none"; }}
                />
                <span className="text-xs text-[#706E6B] whitespace-nowrap">days</span>
              </div>
              <p className="text-[11px] text-[#706E6B] mt-1">
                Default: {data?.defaults?.critical_days} days
              </p>
            </div>

            {/* HIGH */}
            <div>
              <label
                htmlFor="crm-high-days"
                className="block text-xs font-semibold text-[#181818] mb-1.5"
              >
                HIGH threshold
                <span className="ml-1 font-normal text-[#706E6B]">(days before event)</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="crm-high-days"
                  data-testid="input-high-days"
                  type="number"
                  min={1}
                  max={730}
                  value={high}
                  onChange={e => { setHigh(e.target.value); setError(""); }}
                  className="w-full rounded-lg border text-sm px-3 py-2 focus:outline-none"
                  style={{ borderColor: "#DDDBDA", color: "#181818" }}
                  onFocus={e => { e.target.style.borderColor = "#0176D3"; e.target.style.boxShadow = "0 0 0 2px rgba(1,118,211,0.15)"; }}
                  onBlur={e  => { e.target.style.borderColor = "#DDDBDA"; e.target.style.boxShadow = "none"; }}
                />
                <span className="text-xs text-[#706E6B] whitespace-nowrap">days</span>
              </div>
              <p className="text-[11px] text-[#706E6B] mt-1">
                Default: {data?.defaults?.high_days} days
              </p>
            </div>
          </div>

          {/* Validation error */}
          {error && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs"
              style={{ background: "#FEF2F2", borderColor: "#FECACA", color: "#991B1B" }}
              data-testid="urgency-validation-error"
            >
              <AlertCircle size={13} className="shrink-0" />
              {error}
            </div>
          )}

          {/* Last updated */}
          {data?.updated_at && (
            <p className="text-[11px] text-[#706E6B]">
              Last updated: {new Date(data.updated_at).toLocaleString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
              {data.updated_by && <span className="ml-1">by {data.updated_by}</span>}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="btn-save-urgency"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-opacity disabled:opacity-40"
              style={{ background: "#0176D3", color: "#fff" }}
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save Changes
            </button>
            {data?.is_custom && (
              <button
                data-testid="btn-reset-urgency"
                onClick={handleReset}
                disabled={resetting}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors hover:bg-[#F3F2F2] disabled:opacity-40"
                style={{ borderColor: "#DDDBDA", color: "#706E6B" }}
              >
                {resetting
                  ? <Loader2 size={13} className="animate-spin" />
                  : <RotateCcw size={13} />}
                Reset to Default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Section 2 — Co-Pilot Guidance
// ══════════════════════════════════════════════════════════════════════════════

function SectionEditor({ section, sectionData, customMap, open, onToggle, onSaved, toast }) {
  const defaultTips = DEFAULT_SECTION_GUIDE[section]?.tips || [];
  const customSection = customMap[`section:${section}`];
  const effectiveTips = customSection?.tips || defaultTips;

  const [text,    setText]    = useState(tipsToText(effectiveTips));
  const [saving,  setSaving]  = useState(false);
  const [resetting, setResetting] = useState(false);

  // Sync text when customMap changes (silent refresh from parent)
  useEffect(() => {
    const c = customMap[`section:${section}`];
    setText(tipsToText(c?.tips || defaultTips));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customMap, section]);

  const isCustom = !!customSection;
  const dirty = text !== tipsToText(effectiveTips);

  async function handleSave() {
    const tips = textToTips(text);
    if (!tips.length) { toast.show("At least one tip is required", "error"); return; }
    setSaving(true);
    try {
      await crmMdmApi.updateSectionTips(section, tips);
      onSaved();
      toast.show(`${sectionData.label} section tips saved`);
    } catch (e) {
      toast.show(e?.response?.data?.detail || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      await crmMdmApi.resetSectionTips(section);
      onSaved();
      toast.show(`${sectionData.label} section tips reset to defaults`);
    } catch {
      toast.show("Failed to reset", "error");
    } finally {
      setResetting(false);
    }
  }

  return (
    <div
      className="border border-[#DDDBDA] rounded-xl overflow-hidden"
      data-testid={`section-editor-${section}`}
    >
      {/* Section header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-[#F8F7F6] transition-colors text-left"
        onClick={onToggle}
        data-testid={`section-toggle-${section}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-[#181818] truncate">
            {sectionData.label}
          </span>
          {isCustom && <CustomBadge />}
          <span className="text-[11px] text-[#706E6B]">
            ({Object.keys(sectionData.pages || {}).length} page{Object.keys(sectionData.pages || {}).length !== 1 ? "s" : ""})
          </span>
        </div>
        {open
          ? <ChevronUp size={14} className="text-[#706E6B] shrink-0" />
          : <ChevronDown size={14} className="text-[#706E6B] shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 bg-white space-y-4 border-t border-[#F3F2F2]">
          {/* Section-level tips */}
          <div className="pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-[#181818]">
                Section-level tips
                <span className="ml-1 font-normal text-[#706E6B]">(shown in Workflow Guide)</span>
              </label>
              <span className="text-[11px] text-[#706E6B]">
                {textToTips(text).length} / {MAX_TIPS}
              </span>
            </div>
            <TipsEditor
              value={text}
              onChange={setText}
              placeholder={defaultTips.map((t, i) => `${i + 1}. ${t}`).join("\n")}
              testId={`tips-editor-section-${section}`}
            />
            <p className="text-[11px] text-[#706E6B] mt-1">One tip per line. Max {MAX_TIPS} tips, {300} characters each.</p>
          </div>

          {/* Save/reset section tips */}
          <div className="flex items-center gap-2">
            <button
              data-testid={`btn-save-section-${section}`}
              onClick={handleSave}
              disabled={saving || !dirty}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-opacity disabled:opacity-40"
              style={{ background: "#0176D3", color: "#fff" }}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
            {isCustom && (
              <button
                data-testid={`btn-reset-section-${section}`}
                onClick={handleReset}
                disabled={resetting}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border hover:bg-[#F3F2F2] disabled:opacity-40"
                style={{ borderColor: "#DDDBDA", color: "#706E6B" }}
              >
                {resetting ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                Reset
              </button>
            )}
          </div>

          {/* Per-page editors */}
          {Object.entries(sectionData.pages || {}).map(([page, defaultPageTips]) => (
            <PageEditor
              key={page}
              section={section}
              page={page}
              defaultTips={defaultPageTips}
              customItem={customMap[`page:${section}:${page}`]}
              onSaved={onSaved}
              toast={toast}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PageEditor({ section, page, defaultTips, customItem, onSaved, toast }) {
  const effectiveTips = customItem?.tips || defaultTips;
  const [text,     setText]     = useState(tipsToText(effectiveTips));
  const [saving,   setSaving]   = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setText(tipsToText(customItem?.tips || defaultTips));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customItem, page]);

  const isCustom = !!customItem;
  const dirty = text !== tipsToText(effectiveTips);

  async function handleSave() {
    const tips = textToTips(text);
    if (!tips.length && defaultTips.length > 0) {
      toast.show("At least one tip is required", "error");
      return;
    }
    setSaving(true);
    try {
      await crmMdmApi.updatePageTips(section, page, tips.length ? tips : [" "]);
      onSaved();
      toast.show(`"${page}" page tips saved`);
    } catch (e) {
      toast.show(e?.response?.data?.detail || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      await crmMdmApi.resetPageTips(section, page);
      onSaved();
      toast.show(`"${page}" page tips reset to defaults`);
    } catch {
      toast.show("Failed to reset", "error");
    } finally {
      setResetting(false);
    }
  }

  return (
    <div
      className="rounded-lg border p-3 space-y-2"
      style={{ borderColor: "#EEE8E0", background: "#FAFAF9" }}
      data-testid={`page-editor-${section}-${page.replace(/ /g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[#181818]">{page}</span>
          {isCustom && <CustomBadge />}
        </div>
        <span className="text-[11px] text-[#706E6B]">
          {textToTips(text).length} / {MAX_TIPS}
        </span>
      </div>
      <TipsEditor
        value={text}
        onChange={setText}
        placeholder={
          defaultTips.length
            ? defaultTips.map((t, i) => `${i + 1}. ${t}`).join("\n")
            : "No default tips — add custom tips…"
        }
        testId={`tips-editor-page-${section}-${page.replace(/ /g, "-")}`}
      />
      <div className="flex items-center gap-2 pt-0.5">
        <button
          data-testid={`btn-save-page-${section}-${page.replace(/ /g, "-")}`}
          onClick={handleSave}
          disabled={saving || !dirty}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-opacity disabled:opacity-40"
          style={{ background: "#0176D3", color: "#fff" }}
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Save
        </button>
        {isCustom && (
          <button
            data-testid={`btn-reset-page-${section}-${page.replace(/ /g, "-")}`}
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border hover:bg-white disabled:opacity-40"
            style={{ borderColor: "#DDDBDA", color: "#706E6B" }}
          >
            {resetting ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

function CopilotGuidanceSection({ toast }) {
  const [customMap,     setCustomMap]     = useState({});
  const [loading,       setLoading]       = useState(true);
  // Hoisted open state — survives parent re-renders (fixes CERT-11 accordion collapse)
  const [openSections,  setOpenSections]  = useState(new Set());

  const toggleSection = useCallback((section) => {
    setOpenSections(prev => {
      const s = new Set(prev);
      s.has(section) ? s.delete(section) : s.add(section);
      return s;
    });
  }, []);

  const loadHelp = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    crmMdmApi.getCopilotHelp()
      .then(r => setCustomMap(buildHelpLookup(r.data?.items)))
      .catch(() => toast.show("Failed to load Co-Pilot configuration", "error"))
      .finally(() => { if (!silent) setLoading(false); });
  }, [toast]);

  useEffect(() => { loadHelp(); }, [loadHelp]);

  return (
    <div
      className="bg-white border border-[#DDDBDA] rounded-xl overflow-hidden"
      data-testid="copilot-guidance-section"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#F3F2F2] flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#EEF4FF] flex items-center justify-center shrink-0">
          <Compass size={16} className="text-[#0176D3]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-[#181818]">Co-Pilot Guidance</h2>
          <p className="text-xs text-[#706E6B] mt-0.5">
            Customise the workflow tips shown in the Prashasta Assistant drawer for each section and page.
            Tenant overrides replace the system defaults — reset to restore them.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="p-6 flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-[#0176D3]" />
        </div>
      ) : (
        <div className="p-5 space-y-3">
          {/* Info strip */}
          <div
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border text-xs"
            style={{ background: "#F8F7F6", borderColor: "#DDDBDA", color: "#706E6B" }}
          >
            <Info size={13} className="shrink-0 mt-0.5" />
            <span>
              Expand each section to edit tips. <strong>Section-level tips</strong> appear in the Workflow Guide
              accordion. <strong>Page-level tips</strong> appear in the "On this page" panel.
              Entries marked <strong>Custom</strong> override the system defaults.
            </span>
          </div>

          {SECTION_ORDER.map(section => (
            <SectionEditor
              key={section}
              section={section}
              sectionData={DEFAULT_SECTION_GUIDE[section]}
              customMap={customMap}
              open={openSections.has(section)}
              onToggle={() => toggleSection(section)}
              onSaved={() => loadHelp(true)}
              toast={toast}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════════════════
export default function CRMConfiguration() {
  const navigate = useNavigate();
  const toast    = useToast();

  return (
    <div className="min-h-screen bg-[#F8F7F6]" data-testid="crm-configuration-page">
      {/* Page header */}
      <div className="bg-white border-b border-[#DDDBDA] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-[#706E6B] mb-2">
            <button
              onClick={() => navigate("/business-setup")}
              className="hover:text-[#0176D3] transition-colors"
              data-testid="breadcrumb-business-setup"
            >
              Business Setup
            </button>
            <span>/</span>
            <span className="text-[#181818] font-medium">CRM Configuration</span>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/business-setup")}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#DDDBDA] hover:bg-[#F3F2F2] transition-colors"
              data-testid="btn-back-to-setup"
              aria-label="Back to Business Setup"
            >
              <ChevronLeft size={15} className="text-[#706E6B]" />
            </button>
            <div className="w-9 h-9 rounded-lg bg-[#EEF4FF] flex items-center justify-center shrink-0">
              <Settings2 size={18} className="text-[#0176D3]" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#181818]">CRM Configuration</h1>
              <p className="text-xs text-[#706E6B]">
                Urgency thresholds and Co-Pilot guidance · System Administrator only
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <UrgencyThresholdsSection toast={toast} />
        <CopilotGuidanceSection   toast={toast} />
      </div>

      <Toast toast={toast.toast} />
    </div>
  );
}
